import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Blocks,
  BookOpen,
  BotMessageSquare,
  Boxes,
  BrainCircuit,
  CalendarClock,
  ChartColumn,
  ChevronRight,
  CircleDollarSign,
  CircleUser,
  HeartPulse,
  House,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  Library,
  Network,
  Plug,
  Route,
  Scale,
  ScanSearch,
  Settings,
  Shield,
  SlidersHorizontal,
  Vault,
  Workflow,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { useTheme } from './Theme';
import SidebarFooter from './SidebarFooter';
import SidebarBrand from './SidebarBrand';
import { buildPortalOrchestrationHref } from '../../pages/portal/workflows/orchestrationFoUrl';
import { checkPrivilegeAccess } from '../../routes/routePrivilegeMap';
import { AI_MODELS_MODULE_ID } from '../../pages/portal/ai-models/aiModelConstants';
import {
  GOVERNED_MCP_GATEWAY_API_KEYS_ID,
  GOVERNED_MCP_GATEWAY_CONFIGURATION_ID,
  GOVERNED_MCP_GATEWAY_DASHBOARD_ID,
  GOVERNED_MCP_GATEWAY_PATHS,
  GOVERNED_MCP_GATEWAY_TOOLS_ID,
  MCP_RESOURCES_MODULE_ID,
} from '../../pages/portal/governed-mcp-gateway/governedMcpConstants';
import { LLM_GATEWAY_PAGE_ID, LLM_GATEWAY_PATHS } from '../../pages/portal/llm-gateway/llmGatewayConstants';

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

interface RoutePrivilege {
  id: string;
  hasAccess: boolean;
  children?: RoutePrivilege[];
}

interface NavItem {
  to: string;
  icon: LucideIcon;
  label: string;
  id: string;
  children?: NavItem[];
  /** Keep the item active for nested paths under this prefix (e.g. FO iframe routes). */
  matchPrefix?: string;
  title?: string;
}

const SIDEBAR_SECTIONS_STORAGE_KEY = 'portal_sidebar_collapsed_sections';
const MCP_GATEWAY_SECTION = 'AI Gateway Fabric';
const LEGACY_COLLAPSED_SECTIONS: Record<string, string> = {
  'MCP Access Manager': MCP_GATEWAY_SECTION,
  'MCP Gateway': MCP_GATEWAY_SECTION,
};
const ICON_SIZE = 14;
const ICON_STROKE = 1.75;

function createDefaultCollapsedSections(): Set<string> {
  return new Set([
    'Configurations',
    MCP_GATEWAY_SECTION,
    'AI Governance',
    'Access & System',
    'Monitoring',
  ]);
}

function readCollapsedSectionsFromStorage(): Set<string> | null {
  try {
    const raw = localStorage.getItem(SIDEBAR_SECTIONS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return new Set(
      parsed
        .filter((s): s is string => typeof s === 'string')
        .map((s) => LEGACY_COLLAPSED_SECTIONS[s] ?? s)
    );
  } catch {
    return null;
  }
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, setCollapsed }) => {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    () => readCollapsedSectionsFromStorage() ?? createDefaultCollapsedSections()
  );

  useEffect(() => {
    localStorage.setItem(SIDEBAR_SECTIONS_STORAGE_KEY, JSON.stringify(Array.from(collapsedSections)));
  }, [collapsedSections]);

  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const isSectionExpanded = (section: string) => !collapsedSections.has(section);

  const navLinkClass = (isActive: boolean) =>
    `flex items-center ${collapsed ? 'justify-center' : 'gap-2'} px-1.5 py-1 rounded-md transition-all duration-150 text-[11px] font-medium border-l-2 ${
      isActive
        ? isLight
          ? 'border-l-indigo-500 text-indigo-700'
          : 'border-l-indigo-500 text-white'
        : isLight
          ? 'border-l-transparent text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
          : 'border-l-transparent text-gray-300 hover:bg-neutral-800/70 hover:text-white'
    }`;

  const iconChipClass = (isActive: boolean) =>
    `flex h-5 w-5 shrink-0 items-center justify-center rounded ${
      isActive
        ? isLight
          ? 'bg-indigo-50 text-indigo-600'
          : 'bg-indigo-500/15 text-indigo-400'
        : ''
    }`;

  const renderNavIcon = (Icon: LucideIcon, isActive: boolean) => (
    <span className={iconChipClass(isActive)}>
      <Icon size={ICON_SIZE} strokeWidth={ICON_STROKE} />
    </span>
  );

  const routeAccess: RoutePrivilege[] = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user_routes') || '[]');
    } catch {
      return [];
    }
  }, []);

  const hasAccess = (id: string): boolean => checkPrivilegeAccess(id, routeAccess);

  const isNavItemActive = (item: NavItem, isActive: boolean) => {
    if (!item.matchPrefix) return isActive;
    const path = location.pathname;
    return path === item.matchPrefix || path.startsWith(`${item.matchPrefix}/`);
  };

  const renderNavLink = (item: NavItem) => {
    if (!hasAccess(item.id)) return null;

    return (
      <div key={item.to}>
        <NavLink
          to={item.to}
          className={({ isActive }) => navLinkClass(isNavItemActive(item, isActive))}
          title={item.title || (collapsed ? item.label : undefined)}
        >
          {({ isActive }) => {
            const active = isNavItemActive(item, isActive);
            return (
              <>
                {renderNavIcon(item.icon, active)}
                {!collapsed && <span className="min-w-0 truncate leading-[14px]">{item.label}</span>}
              </>
            );
          }}
        </NavLink>

        {item.children && item.children.length > 0 && (
          <div className={`${collapsed ? '' : 'ml-3 pl-2'} border-l ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
            {item.children.map((child) => renderNavLink(child))}
          </div>
        )}
      </div>
    );
  };

  const navItems: { section: string; links: NavItem[] }[] = [

    {
      section: 'Platform',
      links: [
        { to: buildPortalOrchestrationHref(theme), icon: Workflow, label: 'Agent Orchestrations', id: 'b2c3d4e5-f6g7-8901-bcde-f12345678901', matchPrefix: '/portal/orchestration' },
        { to: '/portal/applications', icon: LayoutGrid, label: 'Applications', id: '6f3a0f4c-20b7-4c2a-b4c1-7caaed2358b7' },
        { to: '/portal/catalog', icon: Library, label: 'Catalog', id: 'e8c4a1b2-7d3f-4a91-8e5c-2b6f9d0a4c17' },
        { to: '/portal/chatagent', icon: BotMessageSquare, label: 'Copilots', id: '4e72e1aa-8c6d-4d2b-bd35-0df9a5df4d01' },
        { to: '/portal/agents', icon: Blocks, label: 'Functions', id: 'd918c2ef-0c74-46c7-b50d-dcf4d0d7c59d' },
        { to: '/portal/knowledge-base', icon: BookOpen, label: 'Knowledge Workspace', id: 'h8i9j0k1-l2m3-4567-2345-678901234567' },
        { to: '/portal/analytics-hub', icon: ChartColumn, label: 'Analytics Hub', id: '70b4dff6-7d13-43b0-a6d6-cba6b5b4a8ce' },
      ],
    },
    {
      section: 'Configurations',
      links: [
        { to: '/portal/configurations/data-ingestion', icon: Boxes, label: 'Data Models', id: 'f6g7h8i9-j0k1-2345-f123-456789012345' },
        { to: '/portal/configurations/llm-routing', icon: Route, label: 'LLM Routing', id: 'k1l2m3n4-o5p6-7890-5678-901234567890' },
        { to: '/portal/ai-models', icon: BrainCircuit, label: 'AI Models', id: AI_MODELS_MODULE_ID, matchPrefix: '/portal/ai-models' },
        { to: '/portal/configurations/data-vault', icon: Vault, label: 'Data Vault', id: 'o5p6q7r8-s9t0-1234-9012-345678901234' },
        { to: '/portal/configurations/schedules', icon: CalendarClock, label: 'Schedules', id: 's9t0u1v2-w3x4-5678-3456-789012345678' },
      ],
    },
    {
      section: MCP_GATEWAY_SECTION,
      links: [
        { to: GOVERNED_MCP_GATEWAY_PATHS.dashboard, icon: LayoutDashboard, label: 'Dashboard', id: GOVERNED_MCP_GATEWAY_DASHBOARD_ID },
        { to: LLM_GATEWAY_PATHS.list, icon: Network, label: 'LLM Gateway', id: LLM_GATEWAY_PAGE_ID, matchPrefix: LLM_GATEWAY_PATHS.list },
        { to: GOVERNED_MCP_GATEWAY_PATHS.tools, icon: Wrench, label: 'Tools', id: GOVERNED_MCP_GATEWAY_TOOLS_ID },
        { to: GOVERNED_MCP_GATEWAY_PATHS.configuration, icon: SlidersHorizontal, label: 'Configuration', id: GOVERNED_MCP_GATEWAY_CONFIGURATION_ID },
        { to: GOVERNED_MCP_GATEWAY_PATHS.aiUsage, icon: CircleDollarSign, label: 'AI Usage & Spendings', id: GOVERNED_MCP_GATEWAY_API_KEYS_ID },
        { to: '/portal/mcpresources', icon: Plug, label: 'External MCPs', id: MCP_RESOURCES_MODULE_ID },
      ],
    },
    {
      section: 'AI Governance',
      links: [
        { to: '/portal/ai-governance/policies', icon: Scale, label: 'AI Governance', id: 'g1h2i3j4-k5l6-7890-abcd-ef12345678901' },
      ],
    },
    {
      section: 'Access & System',
      links: [
        { to: '/portal/connections', icon: KeyRound, label: 'Connections & Keys', id: 'e1f2g3h4-i5j6-7890-5678-901234567890' },
        { to: '/portal/access-settings', icon: Shield, label: 'Access Settings', id: 'h4i5j6k7-l8m9-0123-8901-234567890123' },
        { to: '/portal/system-settings', icon: Settings, label: 'System Settings', id: 'm9n0o1p2-q3r4-5678-3456-789012345678' },
        { to: '/portal/my-account', icon: CircleUser, label: 'My Account', id: 'n0o1p2q3-r4s5-6789-4567-890123456789' },
      ],
    },
    {
      section: 'Monitoring',
      links: [
        { to: '/portal/logs', icon: ScanSearch, label: 'AI Observability', id: 'o1p2q3r4-s5t6-7890-5678-901234567890' },
        { to: '/portal/health-status', icon: HeartPulse, label: 'System Health', id: 'o1p2q3r4-s5t6-7890-5678-901234567890' },
      ],
    },
  ];

  useEffect(() => {
    navItems.forEach(({ section, links }) => {
      const hasActiveLink = links.some(
        (link) => location.pathname === link.to || location.pathname.startsWith(`${link.to}/`)
      );
      if (hasActiveLink) {
        setCollapsedSections((prev) => {
          if (!prev.has(section)) return prev;
          const next = new Set(prev);
          next.delete(section);
          return next;
        });
      }
    });
  }, [location.pathname]);

  return (
    <aside
      className={`portal-sidebar ${collapsed ? 'w-12' : 'w-52'} ${isLight ? 'bg-white' : 'bg-neutral-950'} border-r fixed top-0 bottom-0 left-0 z-40 flex flex-col transition-all duration-200`}
    >
      <header className="portal-sidebar-header shrink-0">
        <SidebarBrand collapsed={collapsed} onClick={() => navigate('/home')} />
      </header>

      <div className="portal-sidebar-scroll flex-1 overflow-y-auto overflow-x-hidden pt-1.5">
        <div className="px-1.5 pb-1.5">
          {hasAccess('a1b2c3d4-e5f6-7890-abcd-ef1234567890') && (
            <NavLink to="/portal/home" className={({ isActive }) => navLinkClass(isActive)} title={collapsed ? 'Home' : undefined}>
              {({ isActive }) => (
                <>
                  {renderNavIcon(House, isActive)}
                  {!collapsed && <span className="min-w-0 truncate leading-[14px]">Home</span>}
                </>
              )}
            </NavLink>
          )}
        </div>

        <nav className="space-y-0.5 px-1.5">
          {navItems.map((section) => {
            const visibleLinks = section.links.filter((item) => hasAccess(item.id));
            if (visibleLinks.length === 0) return null;

            return (
              <div key={section.section}>
                {!collapsed && (
                  <button
                    type="button"
                    onClick={() => toggleSection(section.section)}
                    className={`portal-sidebar-section w-full flex items-center justify-between gap-1 mt-2 mb-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold uppercase tracking-wider transition-colors first:mt-1 ${isLight
                      ? 'hover:bg-neutral-50'
                      : 'hover:bg-neutral-800/50'
                      }`}
                    aria-expanded={isSectionExpanded(section.section)}
                    aria-label={`${isSectionExpanded(section.section) ? 'Collapse' : 'Expand'} ${section.section}`}
                  >
                    <span className="truncate text-left">{section.section}</span>
                    <ChevronRight
                      size={11}
                      strokeWidth={2.5}
                      className={`shrink-0 transition-transform duration-200 ease-out ${isSectionExpanded(section.section) ? 'rotate-90' : ''}`}
                    />
                  </button>
                )}
                <div
                  className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isSectionExpanded(section.section) || collapsed ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    }`}
                >
                  <div className="overflow-hidden space-y-0.5">
                    {visibleLinks.map(renderNavLink)}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      <SidebarFooter collapsed={collapsed} setCollapsed={setCollapsed} />
    </aside>
  );
};

export default Sidebar;
